import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikHelpers, isEmptyArray, useFormikContext } from 'formik'
import * as Yup from 'yup'

import { CoverageProviderResponse, getCoverageProviders } from 'core/application/expenses/getCoverageProviders'
import saveCoverageProvider, {
  SaveCoverageProviderRequest,
  SaveCoverageProviderResponse,
} from 'core/application/expenses/saveCoverageProvider'

import { Select } from 'common/components/dataEntry/formik'

import { Button, Option } from 'common/components'
import { parseZone, parseZoneFormat, parseZoneMonthYearFormat } from 'common/dateFormatters'
import { useCommandRequest, useLoginUserV2, useQueryRequest } from 'common/hooks'

import CoverageProviderAddModal from '../coverageProviderAddModal'

import './styles.less'

type CoverageProviderSelectProps = {
  name: string
  date: string
  familyMemberId?: string
  planPeriodId: string
}

const CoverageProviderSelect: React.FC<CoverageProviderSelectProps> = (props) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])

  const [coverageProviders, loadingCoverageProviders, sendGetCoverageProviders] = useQueryRequest<
    CoverageProviderResponse[]
  >([])

  const [showModal, setShowModal] = useState(false)
  const [sendCommand, isCommandLoading] = useCommandRequest<SaveCoverageProviderResponse[]>()
  const {
    user: { productId },
  } = useLoginUserV2()

  const context = useFormikContext<any>()

  useEffect(() => {
    ;(async () => {
      const result = await sendGetCoverageProviders(() =>
        getCoverageProviders(parseZoneFormat(props.date), props.familyMemberId),
      )
      const existingCoverageProvider = result.find((provider) => provider.id === context.values?.coverageProvider)
      context.setFieldValue(props.name, existingCoverageProvider?.id || null)

      result && context.setFieldValue(props.name, result[0]?.id)
    })()
  }, [props.date, props.familyMemberId])

  const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    const request: SaveCoverageProviderRequest = {
      familyMemberId: props.familyMemberId,
      providerName: values.providerName,
      providerStartDate: values.providerStartDate,
    }
    const result = await sendCommand(() => saveCoverageProvider(request))
    if (result.completed) {
      await sendGetCoverageProviders(() => getCoverageProviders(props.date, props.familyMemberId))

      const purchaseDate = parseZone(props.date)
      const found = result.data.find((x) => {
        const startDate = parseZone(x.startDate)
        return startDate.year() === purchaseDate.year() && startDate.month() === purchaseDate.month()
      })
      if (found) {
        context.setFieldValue(props.name, found.id)
      }
      setShowModal(false)
      formikHelpers.setValues({}, false)
    }
  }

  return (
    <>
      {!loadingCoverageProviders && (
        <Select
          label={'Coverage Provider'}
          labelStyles="mb-12"
          name={props.name}
          defaultValue={[context.values.mecCoverageProvider]}
          placeholder={'Select a coverage provider'}
          showRequiredSign={true}
          dropdownRender={(menu) => (
            <>
              {!isEmptyArray(coverageProviders) && menu}
              <Button
                className="w-100 flex flex__align_center"
                type="text"
                icon="fa fa-plus"
                onClick={() => setShowModal(true)}
              >
                {`Add coverage provider`}
              </Button>
            </>
          )}
        >
          {coverageProviders.map((item: CoverageProviderResponse) => (
            <Option key={item.id} value={item.id}>
              {`${item.name}(${parseZoneMonthYearFormat(item.providerStartDate)})`}
            </Option>
          ))}
        </Select>
      )}

      <CoverageProviderAddModal
        showModal={showModal}
        date={props.date}
        setShowModal={setShowModal}
        onSubmit={onSubmit}
        formValidator={formValidator}
        isCommandLoading={isCommandLoading}
      />
    </>
  )
}

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    providerName: Yup.string().nullable().required(translate('translation:general.required')),
    providerStartDate: Yup.date()
      .nullable()
      .typeError(translate('translation:general.required'))
      .required(translate('translation:general.required')),
  })

export default CoverageProviderSelect

export const ADD_COVERAGE_PROVIDER: string = 'add'
